import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  IconButton
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import moment from "moment";
import CalendarCellItem from "../molecule/Dialog/CalendarCellItem";
import MoreClassesModal from "../molecule/Dialog/MoreClassesModal";
import ClassDetailsModal from "../molecule/Dialog/ClassDetailsModal";

const DAYS_OF_WEEK = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

const WebinarCalendarView = ({
  webinarList = [],
  onEditClass,
  onDeleteClass,
  onStartClass,
  getAdminWebinarList,
  onMarkCompleted
}) => {
  const [currentMonth, setCurrentMonth] = useState(moment());
  const [searchTerm] = useState("");

  // State for controlling the "+More" modal
  const [moreModalOpen, setMoreModalOpen] = useState(false);
  const [selectedDateClasses, setSelectedDateClasses] = useState([]);
  const [selectedDateLabel, setSelectedDateLabel] = useState("");
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);

  // Fetch webinars whenever currentMonth changes
  useEffect(() => {
    const startDate = moment(currentMonth).startOf("month").format("YYYY-MM-DD");
    const endDate = moment(currentMonth).endOf("month").format("YYYY-MM-DD");

    if (getAdminWebinarList) {
      getAdminWebinarList({
        startDate,
        endDate,
        month: currentMonth.month() + 1,
        year: currentMonth.year(),
      });
    }
  }, [currentMonth, getAdminWebinarList]);

  const handlePrevMonth = () => {
    setCurrentMonth(moment(currentMonth).subtract(1, "month"));
  };

  const handleNextMonth = () => {
    setCurrentMonth(moment(currentMonth).add(1, "month"));
  };

  const handleOpenMoreModal = (date, classes) => {
    setSelectedDateLabel(moment(date).format("MMMM DD, YYYY"));
    setSelectedDateClasses(classes);
    setMoreModalOpen(true);
  };

  const handleOpenDetailsModal = (meeting) => {
    setSelectedMeeting(meeting);
    setDetailsModalOpen(true);
  };

  const generateCalendarDays = () => {
    const startDay = moment(currentMonth).startOf("month").startOf("week");
    const endDay = moment(currentMonth).endOf("month").endOf("week");

    const day = startDay.clone();
    const calendar = [];

    while (day.isBefore(endDay, "day")) {
      calendar.push({
        date: day.clone(),
        isCurrentMonth: day.isSame(currentMonth, "month"),
        isToday: day.isSame(moment(), "day"),
      });
      day.add(1, "day");
    }
    return calendar;
  };

  const filteredWebinars = webinarList.filter((item) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const courseMatch = item.courses?.some((c) =>
      c.name?.toLowerCase().includes(search)
    );
    return courseMatch || item.id?.toString().includes(search);
  });

  return (
    <Box>
      <Paper variant="outlined" sx={{ borderRadius: "12px", overflow: "hidden" }}>
        {/* Month Navigation Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={2.5}
          borderBottom="1px solid #EAECF0"
        >
          <Typography variant="h6" fontWeight={700} color="#101828">
            {currentMonth.format("MMMM YYYY")}
          </Typography>
          <Box display="flex" gap={1}>
            <IconButton onClick={handlePrevMonth} size="small" sx={{ border: "1px solid #D0D5DD" }}>
              <ChevronLeftIcon />
            </IconButton>
            <IconButton onClick={handleNextMonth} size="small" sx={{ border: "1px solid #D0D5DD" }}>
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Days Header */}
        <Grid container borderBottom="1px solid #EAECF0" bgcolor="#FAFAFA">
          {DAYS_OF_WEEK.map((day) => (
            <Grid
              item
              xs={12 / 7}
              key={day}
              textAlign="center"
              py={1.5}
              sx={{
                fontWeight: 600,
                fontSize: "0.75rem",
                color: "#667085",
                borderRight: "1px solid #EAECF0",
                "&:last-child": { borderRight: "none" },
              }}
            >
              {day}
            </Grid>
          ))}
        </Grid>

        {/* Calendar Cells */}
        <Grid container>
          {generateCalendarDays().map((cell, idx) => {
            const dayClasses = filteredWebinars.filter((item) =>
              moment(item.date).isSame(cell.date, "day")
            );

            return (
              <Grid
                item
                xs={12 / 7}
                key={idx}
                sx={{
                  minHeight: 125,
                  p: 1,
                  borderRight: "1px solid #EAECF0",
                  borderBottom: "1px solid #EAECF0",
                  backgroundColor: cell.isCurrentMonth ? "#FFFFFF" : "#F9FAFB",
                  position: "relative",
                  "&:nth-of-type(7n)": { borderRight: "none" },
                }}
              >
                {/* Date Header */}
                <Box display="flex" justifyContent="flex-end" mb={1}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: cell.isToday ? 700 : 500,
                      color: cell.isCurrentMonth
                        ? cell.isToday
                          ? "#FFFFFF"
                          : "#344054"
                        : "#D0D5DD",
                      bgcolor: cell.isToday ? "#7C2DF4" : "transparent",
                      borderRadius: "50%",
                      width: 24,
                      height: 24,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {cell.date.date()}
                  </Typography>
                </Box>

                {/* Class Items Container */}
                <Box display="flex" flexDirection="column" gap={0.75}>
                  {dayClasses.slice(0, 2).map((item) => (
                    <CalendarCellItem
                      key={item.id}
                      item={item}
                      onClick={() => handleOpenDetailsModal(item)}
                    />
                  ))}

                  {dayClasses.length > 2 && (
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="#7C2DF4"
                      onClick={() => handleOpenMoreModal(cell.date, dayClasses)}
                      sx={{
                        cursor: "pointer",
                        pl: 0.5,
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      +{dayClasses.length - 2} More
                    </Typography>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {/* Modal to display all webinars for the selected day */}
      <MoreClassesModal
        open={moreModalOpen}
        onClose={() => setMoreModalOpen(false)}
        selectedDateLabel={selectedDateLabel}
        classes={selectedDateClasses}
        onSelectClass={handleOpenDetailsModal}
      />

      <ClassDetailsModal
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        selectedMeeting={selectedMeeting}
        onStartClass={onStartClass}
        onMarkCompleted={(id)=>{
          onMarkCompleted(id)
          setDetailsModalOpen(false)
        }}
        onEditClass={onEditClass}
        onDeleteClass={onDeleteClass}
      />
    </Box>
  );
};

export default WebinarCalendarView;